import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';
import Sidebar from '../Sidebar';
import InternalHeader from '../InternalHeader';
import DataTable from 'react-data-table-component';
import '../Appointments/AppointmentDetails.css';
import { FaTrash, FaPlus } from 'react-icons/fa6';
import { FaTrashRestore } from 'react-icons/fa';
import { FaSave } from 'react-icons/fa';
import pdfMake from 'pdfmake/build/pdfmake';

const AppointmentDetailsUser = () => {
    const [isSidebarVisible, setIsSidebarVisible] = useState(true);
    const { id } = useParams();
    const [appointment, setAppointment] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState("Invoice");
    const [treatments, setTreatments] = useState([]);
    const [invoiceItems, setInvoiceItems] = useState([]);
    const [treatmentItems, setTreatmentItems] = useState([]);
    const [notification, setNotification] = useState(null);
    const [notes, setNotes] = useState([
        { medication: "", dosage: "", frequency: "", duration: "" },
    ]);
    const [prescriptionText, setPrescriptionText] = useState("");
    const [patientDetails, setPatientDetails] = useState(null);
    const [inventoryItems, setInventoryItems] = useState([]);
    const [inventoryData, setInventoryData] = useState([]); // To store fetched inventory data

    const handlePrescriptionChange = (event) => {
        setPrescriptionText(event.target.value);
    };

    const generatePDF = (prescriptionText, patientDetails) => {
        // Check if patientDetails is defined
        if (!patientDetails) {
            console.error("Patient details are missing.");
            return;
        }

        const docDefinition = {
            content: [
                { text: 'HGCDENTAL CLINIC', style: 'header' },
                { text: 'DR. HANNA GRACE A. CUSTOSIO', style: 'subheader' },
                { text: '\n' }, // Add some space
                {
                    columns: [
                        {
                            width: '*',
                            text: [
                                { text: 'NAME: ', bold: true },
                                patientDetails.name || 'N/A', // Use patient's name or default to 'N/A'
                            ],
                        },
                        {
                            width: '*',
                            text: [
                                { text: 'ADDRESS: ', bold: true },
                                patientDetails.address || 'N/A', // Use patient's address or default to 'N/A'
                            ],
                        },
                    ],
                },
                { text: '\n' }, // Add some space
                {
                    columns: [
                        {
                            width: '*',
                            text: [
                                { text: 'AGE: ', bold: true },
                                `${patientDetails.ageYear || 0} years, ${patientDetails.ageMonth || 0} months`, // Use patient's age or default to 0
                            ],
                        },
                        {
                            width: '*',
                            text: [
                                { text: 'DATE: ', bold: true },
                                new Date().toLocaleDateString(), // Use current date or a specific date
                            ],
                        },
                    ],
                },
                { text: '\n' }, // Add some space
                { text: prescriptionText, style: 'prescription' },
            ],
            styles: {
                header: { fontSize: 18, bold: true, margin: [0, 0, 0, 10], alignment: 'center' },
                subheader: { fontSize: 14, bold: true, margin: [0, 10, 0, 5], alignment: 'center' },
                prescriptionLabel: { fontSize: 12, bold: true, margin: [0, 10, 0, 5] },
                prescription: { fontSize: 12, margin: [0, 0, 0, 10] },
            },
        };

        pdfMake.createPdf(docDefinition).download('prescription.pdf');
    };

    const handleGeneratePDF = () => {
        if (!patientDetails) {
            console.error("Patient details are not available.");
            return;
        }

        generatePDF(prescriptionText, patientDetails);
    };

    const handleNoteChange = (index, field, value) => {
        const updatedNotes = [...notes];
        updatedNotes[index][field] = value;
        setNotes(updatedNotes);
    };

    const handleAddNoteRow = () => {
        setNotes([...notes, { medication: "", dosage: "", frequency: "", duration: "" }]);
    };

    const handleDeleteNoteRow = (index) => {
        const updatedNotes = notes.filter((_, i) => i !== index);
        setNotes(updatedNotes);
    };

    const canCancelAppointment = (appointmentDate) => {
        const currentDate = new Date();
        const appointmentDateTime = new Date(appointmentDate);
        const timeDifference = appointmentDateTime - currentDate;
        const oneDayInMilliseconds = 24 * 60 * 60 * 1000;

        return timeDifference > oneDayInMilliseconds;
    };

    const toggleSidebar = () => {
        setIsSidebarVisible(!isSidebarVisible);
    };

    const handleTabClick = (tabName) => {
        setActiveTab(tabName);
    };

    const statusSequence = ['Booked', 'Confirmed', 'Checked In', 'Completed', 'Cancelled'];
    const getNextStatus = (currentStatus) => {
        const currentIndex = statusSequence.indexOf(currentStatus);
        return currentIndex >= 0 && currentIndex < statusSequence.length - 1
            ? statusSequence[currentIndex + 1]
            : null;
    };

    const updateInventory = async () => {
        try {
            // 1. Fetch current inventory data
            const response = await fetch('https://api.hgcdental.clinic/api/inventory', {
                credentials: 'include',
            });
            if (!response.ok) throw new Error('Failed to fetch inventory data');
            const currentInventory = await response.json();

            // 2. Create a map of inventory items that need updating
            const inventoryUpdates = inventoryItems.reduce((acc, item) => {
                if (!item.inventoryId) return acc;

                const existingItem = currentInventory.find(i => i._id === item.inventoryId);
                if (!existingItem) return acc;

                // Calculate the difference between current and used quantity
                const quantityUsed = item.quantity;

                return {
                    ...acc,
                    [item.inventoryId]: {
                        ...existingItem,
                        // Subtract the used quantity from current inventory
                        Quantity: Math.max(existingItem.Quantity - quantityUsed, 0),
                        originalQuantity: existingItem.Quantity // Keep track of original for notifications
                    }
                };
            }, {});

            // 3. Process updates and send notifications if needed
            for (const [inventoryId, updatedItem] of Object.entries(inventoryUpdates)) {
                try {
                    console.log(`Updating inventory for ID: ${inventoryId}`);

                    const putResponse = await fetch(`https://api.hgcdental.clinic/api/inventory/${inventoryId}`, {
                        method: 'PUT',
                        credentials: 'include',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            Name: updatedItem.Name,
                            Quantity: updatedItem.Quantity,
                            Price: updatedItem.Price,
                            Unit: updatedItem.Unit,
                            StoreName: updatedItem.StoreName,
                            StoreContact: updatedItem.StoreContact,
                        }),
                    });

                    if (!putResponse.ok) {
                        console.error(`Failed to update inventory for ID: ${inventoryId}`);
                        continue;
                    }

                    console.log(`Successfully updated inventory for ID: ${inventoryId}`);

                    // Check if we need to send low stock notification
                    if (updatedItem.Quantity < updatedItem.CriticalStock) {
                        await fetch('https://api.hgcdental.clinic/api/notifications', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                type: 'low_stock',
                                message: `⚠️ Inventory Alert: ${updatedItem.Name} is below critical stock.`,
                                date: new Date().toISOString(),
                            }),
                        });
                    }
                } catch (err) {
                    console.error(`Error updating inventory item ${inventoryId}:`, err);
                }
            }

            alert('Inventory updated successfully based on actual usage');
        } catch (err) {
            console.error('Error in updateInventory:', err);
            alert('Error updating inventory: ' + err.message);
        }
    };

    const proceedToNextStatus = async () => {
        const nextStatus = getNextStatus(appointment.status);
        if (nextStatus) {
            try {
                const updateData = { status: nextStatus };
                if (nextStatus === 'Checked In') {
                    updateData.checkinTime = new Date().toLocaleString();
                }
                const response = await fetch(`https://api.hgcdental.clinic/api/appointments/id/${id}/status`, {
                    method: 'PATCH',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(updateData),
                });

                if (!response.ok) throw new Error('Failed to update appointment status');
                const data = await response.json();

                setAppointment(prev => ({
                    ...prev,
                    status: data.status,
                    checkinTime: data.checkinTime || prev.checkinTime,
                }));

                if (nextStatus === 'Completed') {
                    await updateInventory();
                }
            } catch (err) {
                setError(err.message);
            }
        }
    };

    useEffect(() => {
        const fetchAppointment = async () => {
            try {
                // Fetch the appointment details
                const response = await fetch(`https://api.hgcdental.clinic/api/appointments/id/${id}`, {
                    credentials: 'include',
                });
                if (!response.ok) throw new Error('Failed to fetch appointment details');
                const data = await response.json();

                // Format appointment date and checkin time
                const formattedDate = new Date(data.date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                });
                const formattedCheckinTime = data.checkinTime
                    ? format(new Date(data.checkinTime), 'MMM. dd, yyyy - hh:mm a')
                    : 'Not Checked In';

                // Fetch patient details using name and email
                const patientResponse = await fetch(`https://api.hgcdental.clinic/api/patients?name=${encodeURIComponent(data.patientName)}&email=${encodeURIComponent(data.email)}`, {
                    credentials: 'include',
                });
                if (!patientResponse.ok) throw new Error('Failed to fetch patient details');
                const patientData = await patientResponse.json();

                // Set appointment details
                setAppointment({
                    date: formattedDate,
                    appointmentId: data.date,
                    patientID: data.patientID,
                    patient: patientData[0],
                    dentist: data.dentist,
                    Treatmentname: data.reason,
                    Treatmentcost: data.cost || 'Not Available',
                    startTime: data.startTime,
                    endTime: data.endTime,
                    checkinTime: formattedCheckinTime,
                    status: data.status || 'Booked',
                    email: data.email,
                });

                // Set patient details
                if (patientData.length > 0) {
                    setPatientDetails(patientData[0]);
                }

                // Set invoice items if they exist
                setInvoiceItems(data.invoice || []);

                // Set inventory items if they exist
                setInventoryItems(data.inventory || []);

                // Check if notes exist in the appointment data and set them
                if (data.notes && data.notes.length > 0) {
                    setNotes(data.notes);
                } else {
                    // Initialize with empty note if no notes exist
                    setNotes([{ medication: "", dosage: "", frequency: "", duration: "" }]);
                }

            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        const fetchTreatments = async () => {
            try {
                const response = await fetch('https://api.hgcdental.clinic/api/treatments');
                if (!response.ok) throw new Error('Failed to fetch treatments');
                const data = await response.json();
                setTreatments(data);
            } catch (err) {
                console.error(err.message);
            }
        };

        const fetchInventory = async () => {
            try {
                const response = await fetch('https://api.hgcdental.clinic/api/inventory', {
                    credentials: 'include',
                });
                if (!response.ok) throw new Error('Failed to fetch inventory');
                const data = await response.json();
                setInventoryData(data);
            } catch (err) {
                console.error(err.message);
            }
        };

        fetchAppointment();
        fetchTreatments();
        fetchInventory();
    }, [id]);

    const handleResetInventoryItem = (index) => {
        const itemToReset = inventoryItems[index];
        if (!itemToReset.treatmentId) return;

        const treatment = treatments.find(t => t._id === itemToReset.treatmentId);
        if (!treatment || !treatment.Inventory) return;

        const invItem = treatment.Inventory.find(i => i.inventoryId === itemToReset.inventoryId);
        if (!invItem) return;

        const invoiceItem = invoiceItems.find(i => i.treatmentId === treatment._id);
        const quantity = invItem.Quantity * (invoiceItem?.quantity || 1);

        const updatedItems = inventoryItems.map((item, i) =>
            i === index
                ? {
                    ...item,
                    quantity,
                    total: item.price * quantity,
                    isModified: false
                }
                : item
        );
        setInventoryItems(updatedItems);
    };

    const handleInventoryChange = (index, inventoryId) => {
        const selectedInventory = inventoryData.find(item => item._id === inventoryId);
        if (selectedInventory) {
            const updatedItems = inventoryItems.map((item, i) =>
                i === index
                    ? {
                        ...item,
                        inventoryId,
                        name: selectedInventory.Name,
                        quantity: 1, // Default quantity
                        price: selectedInventory.Price,
                        unit: selectedInventory.Unit,
                        total: selectedInventory.Price // Initial total
                    }
                    : item
            );
            setInventoryItems(updatedItems);
        }
    };

    const handleInventoryQuantityChange = (index, quantity) => {
        const updatedItems = inventoryItems.map((item, i) =>
            i === index
                ? {
                    ...item,
                    quantity: Math.max(quantity, 1),
                    total: item.price * Math.max(quantity, 1),
                    isModified: true // Mark as manually modified
                }
                : item
        );
        setInventoryItems(updatedItems);
    };

    const inventoryColumns = [
        {
            name: 'Item Name',
            cell: (row, index) => (
                <select
                    value={row.inventoryId || ''}
                    onChange={(e) => handleInventoryChange(index, e.target.value)}
                >
                    <option value="" disabled>Select Item</option>
                    {inventoryData.map((item) => (
                        <option key={item._id} value={item._id}>
                            {item.Name}
                        </option>
                    ))}
                </select>
            ),
        },
        {
            name: 'Price',
            selector: (row) => `₱${(row.price ?? 0).toFixed(2)}`,
            sortable: true,
        },
        {
            name: 'Unit',
            selector: (row) => row.unit || 'N/A',
            sortable: true,
        },
        {
            name: 'Quantity',
            cell: (row, index) => (
                <input
                    type="number"
                    value={row.quantity}
                    min="1"
                    onChange={(e) => handleInventoryQuantityChange(index, parseInt(e.target.value, 10))}
                />
            ),
            sortable: true,
        },
        {
            name: 'Total',
            selector: (row) => `₱${(row.total ?? 0).toFixed(2)}`,
            sortable: true,
        },
        {
            name: 'Actions',
            cell: (row, index) => (
                <div>
                    <FaTrash
                        className="action-icon"
                        style={{ color: 'red', cursor: 'pointer', marginRight: '10px' }}
                        onClick={() => handleDeleteInventoryItem(index)}
                    />
                    {/* {row.isModified && (
                        <FaTrashRestore
                            className="action-icon"
                            style={{ color: 'blue', cursor: 'pointer' }}
                            onClick={() => handleResetInventoryItem(index)}
                        />
                    )} */}
                </div>
            ),
        }
    ];

    const addInventoryItem = () => {
        setInventoryItems([
            ...inventoryItems,
            {
                inventoryId: null,
                name: '',
                price: 0,
                unit: '',
                quantity: 1,
                total: 0
            },
        ]);
    };

    const handleDeleteInventoryItem = (index) => {
        const updatedItems = inventoryItems.filter((_, i) => i !== index);
        setInventoryItems(updatedItems);
    };

    const calculateInventoryTotal = () =>
        inventoryItems.reduce((sum, item) => sum + item.total, 0);

    const handleSaveInventory = async () => {
        try {
            const response = await fetch(`https://api.hgcdental.clinic/api/appointments/${id}/inventory`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({ inventory: inventoryItems })
            });

            if (!response.ok) throw new Error('Failed to save inventory items');

            const data = await response.json();
            // Update local state with the saved data
            setInventoryItems(data.inventory || inventoryItems);
            alert('Inventory saved successfully!');
        } catch (err) {
            console.error('Error saving inventory items:', err);
            // setNotification('Error saving inventory items: ' + err.message);
        }
    };

    const calculateGrandTotal = () =>
        invoiceItems.reduce((sum, item) => sum + item.total, 0);

    const handleDeleteItem = (index) => {
        const itemToDelete = invoiceItems[index];
        const updatedItems = invoiceItems.filter((_, i) => i !== index);
        setInvoiceItems(updatedItems);

        // Remove inventory items associated with this treatment
        if (itemToDelete.treatmentId) {
            const updatedInventory = inventoryItems.filter(
                item => item.treatmentId !== itemToDelete.treatmentId
            );
            setInventoryItems(updatedInventory);
        }
    };

    const getTreatmentInventory = (treatmentId) => {
        const treatment = treatments.find(t => t._id === treatmentId);
        if (!treatment || !treatment.Inventory) return [];

        return treatment.Inventory.map(invItem => {
            const inventoryItem = inventoryData.find(i => i._id === invItem.inventoryId);
            return {
                inventoryId: invItem.inventoryId,
                name: inventoryItem?.Name || '',
                price: inventoryItem?.Price || 0,
                unit: inventoryItem?.Unit || '',
                quantity: invItem.Quantity,
                total: (inventoryItem?.Price || 0) * invItem.Quantity,
                treatmentId: treatment._id,
                isEditable: true
            };
        });
    };

    const handleDeleteTreatment = (index) => {
        const updatedItems = treatmentItems.filter((_, i) => i !== index);
        setTreatmentItems(updatedItems);
    };

    const handleSaveNotes = async () => {
        try {
            const response = await fetch(`https://api.hgcdental.clinic/api/appointments/${id}/notes`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ notes })
            });

            if (!response.ok) throw new Error('Failed to save notes');

            alert('Prescriptions saved successfully!');
        } catch (err) {
            console.error(err);
            alert('Error saving notes');
        }
    };

    const handleTreatmentChange = (index, treatmentId) => {
        const selectedTreatment = treatments.find(t => t._id === treatmentId);
        if (selectedTreatment) {
            // Update invoice items
            const updatedItems = invoiceItems.map((item, i) =>
                i === index
                    ? {
                        ...item,
                        treatmentId,
                        legend: selectedTreatment.Legend,
                        treatmentName: selectedTreatment.Treatmentname,
                        treatmentCost: selectedTreatment.Cost,
                        total: selectedTreatment.Cost * item.quantity,
                    }
                    : item
            );
            setInvoiceItems(updatedItems);

            // Get treatment inventory
            const treatmentInventory = getTreatmentInventory(treatmentId);

            // Preserve manually modified items
            const otherInventory = inventoryItems.filter(
                item => !item.treatmentId ||
                    item.treatmentId !== treatmentId ||
                    item.isModified
            );

            // Add new inventory items
            const newInventory = treatmentInventory.filter(newItem =>
                !otherInventory.some(existingItem =>
                    existingItem.inventoryId === newItem.inventoryId &&
                    existingItem.treatmentId === newItem.treatmentId
                )
            );

            setInventoryItems([...otherInventory, ...newInventory]);
        }
    };
    const handleQuantityChange = (index, quantity) => {
        const updatedItems = invoiceItems.map((item, i) =>
            i === index
                ? {
                    ...item,
                    quantity: Math.max(quantity, 1),
                    total: item.treatmentCost * Math.max(quantity, 1),
                }
                : item
        );
        setInvoiceItems(updatedItems);
    };

    useEffect(() => {
        setPrescriptionText(generatePrescriptionText());
    }, [notes]);

    const handleSaveInvoice = async () => {
        try {
            // Prepare inventory updates - keep manual changes
            const finalInventory = inventoryItems.map(item => {
                if (item.isModified) {
                    return item; // Keep manual changes
                }

                // For non-modified items, use treatment defaults
                const treatment = treatments.find(t => t._id === item.treatmentId);
                if (treatment && treatment.Inventory) {
                    const invItem = treatment.Inventory.find(i => i.inventoryId === item.inventoryId);
                    if (invItem) {
                        const invoiceItem = invoiceItems.find(i => i.treatmentId === treatment._id);
                        return {
                            ...item,
                            quantity: invItem.Quantity * (invoiceItem?.quantity || 1),
                            total: item.price * invItem.Quantity * (invoiceItem?.quantity || 1)
                        };
                    }
                }
                return item;
            });

            // Save both invoice and inventory
            const response = await fetch(`https://api.hgcdental.clinic/api/appointments/${id}/invoice-with-inventory`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    invoice: invoiceItems,
                    inventory: finalInventory
                })
            });
            alert('Treatments saved successfully!');
            if (!response.ok) throw new Error('Failed to save invoice and inventory');

            const data = await response.json();
            setInvoiceItems(data.invoice || invoiceItems);
            setInventoryItems(data.inventory || finalInventory);
        } catch (err) {
            console.error('Error saving:', err);
            setNotification('Error saving: ' + err.message);
        }
    };

    const checkInventoryAvailability = (treatmentId) => {
        const treatment = treatments.find(t => t._id === treatmentId);
        if (!treatment || !treatment.Inventory) return true;

        return treatment.Inventory.every(invItem => {
            const inventoryItem = inventoryData.find(i => i._id === invItem.inventoryId);
            return inventoryItem && inventoryItem.Quantity >= invItem.Quantity;
        });
    };

    const handleSaveTreatments = async () => {
    };

    const addInvoiceItem = () => {
        setInvoiceItems([
            ...invoiceItems,
            {
                treatmentId: null,
                legend: '',
                treatmentName: '',
                treatmentCost: 0,
                quantity: 1,
                total: 0,
            },
        ]);
    };

    const invoiceColumns = [
        {
            name: 'Treatment Name',
            cell: (row, index) => (
                <select
                    value={row.treatmentId || ''}
                    onChange={(e) => handleTreatmentChange(index, e.target.value)}
                >
                    <option value="" disabled>
                        Select Treatment
                    </option>
                    {treatments.map((treatment) => (
                        <option key={treatment._id} value={treatment._id}>
                            {treatment.Treatmentname}
                        </option>
                    ))}
                </select>
            ),
        },
        {
            name: 'Legend',
            selector: (row) => row.legend || 'N/A', // Display the legend as text or 'N/A' if undefined
            sortable: true,
        },
        {
            name: 'Treatment Cost',
            selector: (row) => `₱${(row.treatmentCost ?? 0).toFixed(2)}`,
            sortable: true,
        },
        {
            name: 'Quantity',
            cell: (row, index) => (
                <input
                    type="number"
                    value={row.quantity}
                    min="1"
                    onChange={(e) => handleInventoryQuantityChange(index, parseInt(e.target.value, 10))}
                    disabled={!row.isEditable} // Only allow editing if flagged
                />
            ),
            sortable: true,
        },
        {
            name: 'Total',
            selector: (row) => `₱${(row.total ?? 0).toFixed(2)}`,
            sortable: true,
        },
        {
            name: 'Actions',
            cell: (row, index) => (
                <FaTrash
                    className="action-icon"
                    style={{ color: 'red', cursor: 'pointer' }}
                    onClick={() => handleDeleteItem(index)} // Attach the delete handler directly
                />
            ),
        },
    ];

    const addTreatmentsItem = () => {
        setTreatmentItems([
            ...treatmentItems,
            {
                treatmentId: null,
                legend: '',
                treatmentName: '',
            },
        ]);
    };

    const treatmentColumns = [
        {
            name: 'Treatment Name',
            cell: (row, index) => (
                <select
                    value={row.treatmentId || ''}
                    onChange={(e) => handleTreatmentChange(index, e.target.value)}
                >
                    <option value="" disabled>
                        Select Treatment
                    </option>
                    {treatments.map((treatment) => (
                        <option key={treatment._id} value={treatment._id}>
                            {treatment.Treatmentname}
                        </option>
                    ))}
                </select>
            ),
        },
        {
            name: 'Legend',
            selector: (row) => row.legend || 'N/A', // Display the legend as text or 'N/A' if undefined
            sortable: true,
        },
        {
            name: 'Actions',
            cell: (row, index) => (
                <FaTrash
                    className="action-icon"
                    style={{ color: 'red', cursor: 'pointer' }}
                    onClick={() => handleDeleteTreatment(index)} // Attach the delete handler directly
                />
            ),
        },
    ];

    const handleCancelAppointment = async () => {
        if (!canCancelAppointment(appointment.date)) {
            setNotification('Cancellation is only allowed at least one day before the appointment date.');
            return;
        }

        try {
            const response = await fetch(`https://api.hgcdental.clinic/api/appointments/id/${id}/status`, {
                method: 'PATCH',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ status: 'Cancelled' }),
            });

            if (!response.ok) throw new Error('Failed to cancel appointment');
            const data = await response.json();

            setAppointment(prev => ({
                ...prev,
                status: data.status,
            }));

            setNotification('Appointment cancelled successfully.');
        } catch (err) {
            setError(err.message);
        }
    };

    const generatePrescriptionText = () => {
        return notes
            .map((note, index) => {
                if (!note.medication && !note.dosage && !note.frequency && !note.duration) {
                    return ""; // Skip empty rows
                }
                return `${index + 1}. ${note.medication} ${note.dosage}\n   - ${note.frequency}\n   - Duration: ${note.duration}`;
            })
            .filter((line) => line !== "") // Remove empty lines
            .join("\n\n"); // Join with double newlines for spacing
    };

    const customStyles = {
        rows: {
            style: {
                borderBottom: '1px solid #ddd',
            },
        },
        headCells: {
            style: {
                backgroundColor: '#007bff',
                color: 'white',
                fontWeight: 'bold',
                padding: '15px 10px',
                textAlign: 'center',
            },
        },
        cells: {
            style: {
                padding: '12px 10px',
                borderRight: '1px solid #ddd',
            },
        },
        pagination: {
            style: {
                display: 'flex',
                justifyContent: 'center',
                padding: '10px',
                backgroundColor: '#f4f4f4',
            },
        },
        sortIcon: {
            style: {
                color: '#007bff',
                fontSize: '18px',
            },
        },
    };

    if (loading) return <p>Loading...</p>;
    if (error) return <p style={{ color: 'red' }}>{error}</p>;

    const nextStatus = getNextStatus(appointment.status);

    return (
        <div className={`appointments-page ${isSidebarVisible ? 'sidebar-open' : 'sidebar-closed'}`}>
            <InternalHeader moduleName="Appointments" toggleSidebar={toggleSidebar} />
            <div className="appointments-container">
                <Sidebar isSidebarVisible={isSidebarVisible} />
                <div className="appointments-form">
                    <div className="appointments-header">
                        {(appointment.status === 'Confirmed' || appointment.status === 'Booked') && (
                            <button
                                className="next-status-button-1"
                                onClick={handleCancelAppointment}
                            >
                                Cancel
                            </button>
                        )}
                        <div className="status-section">
                            <div className="status-options">
                                {statusSequence.map((status) => (
                                    <div
                                        key={status}
                                        className={`status-option ${appointment.status === status ? 'current' : ''}`}
                                    >
                                        {status}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {notification && (
                            <div className="notification">
                                {notification}
                                <button onClick={() => setNotification(null)}>Close</button>
                            </div>
                        )}
                    </div>
                    <div className="appointment-details">
                        <h2><strong>{appointment.date}</strong></h2>
                        <p><strong>Patient ID:</strong> {appointment.patientID}</p>
                        <p><strong>Patient:</strong> {appointment.patient?.name}</p>
                        <p><strong>Email:</strong> {appointment.email}</p>
                        <p><strong>Dentist:</strong> {appointment.dentist}</p>
                        <p><strong>Reason:</strong> {appointment.Treatmentname}</p>
                        <p><strong>Start Time:</strong> {appointment.startTime}</p>
                        <p><strong>End Time:</strong> {appointment.endTime}</p>
                        <p><strong>Check In Time:</strong> {appointment.checkinTime}</p>

                        {/* Tab Navigation */}
                        <div className="tab-navigation">
                            <span
                                onClick={() => handleTabClick('Invoice')}
                                className={`tab ${activeTab === 'Invoice' ? 'active' : ''}`}
                            >
                                Treatments
                            </span>
                        </div>

                        {/* Tab Content */}
                        <div className="tab-content">
                            {activeTab === 'Invoice' && (
                                <div className="invoice-tab">
                                    <div className="table-form-table">
                                        <DataTable
                                            columns={invoiceColumns}
                                            data={invoiceItems}
                                            highlightOnHover
                                            striped
                                            customStyles={customStyles}
                                        />
                                    </div>
                                    <div className="grand-total">
                                        <h3>Grand Total: ₱{(calculateGrandTotal() ?? 0).toFixed(2)}</h3>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AppointmentDetailsUser;